/**
 * Qui l'on propose d'affecter en face d'un besoin, et pourquoi.
 *
 * *Signalé par Chris le 03/09/2026 : « dans le plan de charge exploitation et transport, lorsqu'on
 * veut affecter une ressource, si on clique sur le menu déroulant toute la liste apparaît ; il
 * faudrait filtrer la liste pertinente qui correspond au générique, cela faciliterait grandement
 * la tâche. »*
 *
 * ## Le filtre existait ; c'est son REPLI qui déversait le parc
 *
 * La liste est filtrée sur le code générique du besoin depuis la 017. Mais quand le référentiel
 * ne rattache **aucune** ressource à ce code, elle s'ouvrait en entier — « mieux vaut chercher
 * que ne rien pouvoir affecter ». Mesuré à l'écran le 03/09/2026 sur les deux chantiers de ROY :
 * quatorze codes proposaient de 1 à 28 ressources, et un seul — `04-03-18-100` NIVELEUSE — en
 * proposait **216**. Le repli n'était pas un cas rare, c'était le cas visible.
 *
 * Et il n'est pas rare en général : sur le référentiel réel, 25 codes de main d'œuvre sur 40 et
 * 16 codes matériel sur 62 ne portent aucune ressource. Le supprimer laisserait donc des lignes
 * qu'on ne pourrait plus affecter du tout.
 *
 * ## La famille, et non un préfixe deviné
 *
 * Une niveleuse existe au parc : elle est rattachée à `04-03-18-200` quand le déboursé chiffre
 * `04-03-18-100`. Les deux codes appartiennent à la même **famille** `04-03-18` — et cette
 * famille n'est pas déduite en coupant la chaîne : elle est **donnée par les deux référentiels**,
 * `famille_code` côté ressources génériques et `code_categorie` côté fiche machine. C'est un
 * rapprochement par identifiant, la règle sortie de la panne du 16/08/2026 ; découper un code
 * d'ERP à un tiret serait la convention d'un client écrite en dur, donc le fork qui commence.
 *
 * ## Ce que l'écran doit dire
 *
 * Une liste dont on ne sait pas ce qu'elle contient est le défaut d'origine : 216 machines
 * ressemblaient à un filtre absent, et deux niveleuses ressemblent à un parc qui n'en compte que
 * deux. Chaque repli porte donc son **titre**, qui dit d'où viennent les propositions et ce qui
 * manquait pour faire mieux.
 */

/** D'où viennent les ressources proposées. */
export type Provenance = "code" | "famille" | "tout";

/** Le second groupe du menu : ce que la liste principale ne propose pas. */
export type Extension<T> = {
  readonly titre: string;
  readonly candidats: readonly T[];
};

export type Proposition<T> = {
  readonly provenance: Provenance;
  /** Le titre du groupe à l'écran, ou `null` quand la liste est celle du code exact. */
  readonly titre: string | null;
  readonly candidats: readonly T[];
  /** Le reste du référentiel, ouvert sous son propre titre. `null` = il n'y a rien à ajouter. */
  readonly extension: Extension<T> | null;
};

/**
 * La cascade : le code exact, puis sa famille, puis tout le référentiel.
 *
 * Générique parce que la règle est la même pour un homme et pour une machine — seules les listes
 * changent. Écrite hors du composant parce que c'est la seule part qui se vérifie sans navigateur,
 * comme `remplacement.ts`.
 *
 * *Le cas « rien nulle part » rend `tout` avec une liste vide et le titre qui le dit : c'est ce
 * qu'il faut afficher quand le référentiel entier ne répond pas, plutôt qu'un menu muet.*
 */
export function choisirCandidats<T>({
  code,
  duCode,
  deLaFamille,
  tous,
  familleLibelle,
}: {
  /** Le code du besoin, tel qu'il s'affiche sur la ligne. */
  readonly code: string;
  readonly duCode: readonly T[];
  readonly deLaFamille: readonly T[];
  readonly tous: readonly T[];
  /** Le libellé de la famille au référentiel — `NIVELEUSE`. Nul si le code n'en porte pas. */
  readonly familleLibelle: string | null;
}): Proposition<T> {
  if (duCode.length > 0) {
    return { provenance: "code", titre: null, candidats: duCode, extension: null };
  }
  if (deLaFamille.length > 0) {
    return {
      provenance: "famille",
      // Le titre nomme les deux : ce qui manque (le code exact) et ce qu'on propose à la place
      // (la famille). Sans le premier, on croit que le référentiel ne connaît que ça.
      titre:
        familleLibelle === null
          ? `Aucune sur ${code} — même famille`
          : `Aucune sur ${code} — famille ${familleLibelle}`,
      candidats: deLaFamille,
      extension: null,
    };
  }
  return {
    provenance: "tout",
    titre: `Aucune sur ${code} ni dans sa famille — tout le référentiel`,
    candidats: tous,
    extension: null,
  };
}

/**
 * Ouvrir le reste du référentiel sous la liste filtrée, sans toucher à la liste filtrée.
 *
 * *Demande de Chris, 10/09/2026 : « pour les ressources main d'œuvre, je préfère quand on affecte
 * que tu fasses apparaître une extension autres ressources, car si un salarié n'est pas bien
 * affecté sur un code main d'œuvre il faut pouvoir juste aller le chercher dans un autre code.
 * Donc tu laisses la présentation telle quelle est dans le menu déroulant et si on ne trouve pas
 * le salarié on peut ouvrir tout pour le chercher si besoin. »*
 *
 * Le filtre du 03/09 réglait le déversement du parc ; il posait en échange une **impasse**, celle
 * du salarié rattaché au mauvais code de main d'œuvre — invisible dans la liste, et donc
 * inaffectable tant que quelqu'un n'était pas allé corriger le référentiel maître. Ce n'est pas un
 * cas de bord : le rattachement `code_mo` vient de l'ERP, la planification n'y écrit rien, et une
 * ligne de chantier ne peut pas attendre une correction ailleurs.
 *
 * Le repli en cascade ne pouvait pas y répondre : il ne s'ouvre que lorsque la liste filtrée est
 * **vide**, alors que c'est précisément quand elle propose trois noms — sans celui qu'on cherche —
 * qu'on est bloqué. D'où un second groupe plutôt qu'un troisième cran de la cascade.
 *
 * Deux cas où l'on ne rend rien de plus, et ils ont la même raison — **ne jamais écrire deux fois
 * la même personne dans un menu** : la proposition qui a déjà déversé tout le référentiel
 * (`provenance` vaut `tout`), et la liste d'appoint vide, qui n'afficherait qu'un titre sous lequel
 * il n'y a rien à choisir.
 *
 * *`autres` se calcule chez l'appelant, seul à connaître ce qui identifie une ressource : la
 * dédoublonner ici demanderait une clé, et une clé devinée sur un type générique est le genre de
 * supposition qui se paie deux mois plus tard.*
 */
export function ouvrirLeReferentiel<T>(
  proposition: Proposition<T>,
  autres: readonly T[],
  titre = "Autres ressources",
): Proposition<T> {
  if (proposition.provenance === "tout" || autres.length === 0) return proposition;
  return { ...proposition, extension: { titre, candidats: autres } };
}

/**
 * Tout ce que le menu propose, les deux groupes confondus.
 *
 * Le menu rend un identifiant, et il faut le retrouver pour savoir quoi affecter. Chercher dans
 * les seuls `candidats` laisserait le second groupe **cliquable et sans effet** — la pire des
 * pannes, celle qui ne dit rien.
 */
export function tousLesCandidats<T>(proposition: Proposition<T>): readonly T[] {
  return proposition.extension === null
    ? proposition.candidats
    : [...proposition.candidats, ...proposition.extension.candidats];
}

/**
 * La famille d'une machine vue par le référentiel des génériques.
 *
 * Deux chemins, et le premier a la priorité : le **code générique** de la machine, s'il est connu
 * du référentiel, porte déjà sa famille — c'est la valeur que l'administrateur tient à l'écran.
 * À défaut, la fiche machine porte la sienne (`code_categorie`), et c'est elle qui sauve le cas
 * qui nous occupe : la machine est rattachée à un code que le référentiel des génériques ne
 * connaît pas.
 *
 * Rend `null` quand aucun des deux ne répond — et un `null` ne se rapproche de rien : deux
 * familles inconnues ne sont pas la même famille.
 */
export function familleDeLaMachine(
  machine: { readonly generique: string | null; readonly codeCategorie: string | null },
  familleDuGenerique: (code: string) => string | null,
): string | null {
  const parLeGenerique = machine.generique === null ? null : familleDuGenerique(machine.generique);
  if (parLeGenerique !== null && parLeGenerique !== "") return parLeGenerique;
  return machine.codeCategorie === null || machine.codeCategorie === ""
    ? null
    : machine.codeCategorie;
}
